/**
 * What: Settings connection source and merchant actions, including retry of an existing cleanup obligation.
 * Transport / data-flow: Pure lifecycle checks over the shared authenticated readiness query; no I/O.
 * Invariants: Pending/unconfirmed cleanup is not completion; an action always uses the displayed activation.
 */
import { definePage } from '../page-declaration'
import type { ShopifyConnectionSnapshot } from '../hooks/useShopifyConnection'
import type { TestFeedConnectionStatus } from '../types'
import {
  isTestFeedConnectionStatus,
  normalizeTestFeedConnectionStatus,
} from '../../src/shared/testfeed-connection-status-contract'
import { resolveTestFeedReadinessPresentation } from '../components/TestFeedReadinessCard'
import { shopifyComponentCopy } from '@testfeed/product-language'

const copy = shopifyComponentCopy.settingsIndex

const connectionCheck = {
  safeParse(
    value: unknown,
  ): { success: true; data: Pick<ShopifyConnectionSnapshot, 'verifiedShop' | 'readiness'> } | { success: false } {
    if (!value || typeof value !== 'object' || !('verifiedShop' in value) || !('readiness' in value))
      return { success: false }
    if (typeof value.verifiedShop !== 'string' || !isTestFeedConnectionStatus(value.readiness))
      return { success: false }
    if (normalizeTestFeedConnectionStatus(value.readiness).code === 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE')
      return { success: false }
    // The query's bootstrap verified this shop; all actionable target fields must belong to it.
    if (value.readiness.externalAccountId && value.readiness.externalAccountId !== value.verifiedShop)
      return { success: false }
    return { success: true, data: { verifiedShop: value.verifiedShop, readiness: value.readiness } }
  },
}

/** Checked readiness and explicit actions share one busy/error owner; refresh never resubmits unlink. */
export const page = definePage({
  name: 'settings',
  sources: { connection: { required: true, check: connectionCheck } },
  actions: {
    connect: {
      saves: false,
      interaction: { textEditing: 'block' },
      check: (readiness: TestFeedConnectionStatus) =>
        resolveTestFeedReadinessPresentation(readiness, false).canConnect ? null : 'not-allowed',
      feedback: { failure: copy.connectFailed },
    },
    disconnect: {
      saves: false,
      interaction: { textEditing: 'block' },
      check: (readiness: TestFeedConnectionStatus) =>
        readiness.connectionStatus === 'active' && readiness.connectionId && readiness.connectionActivatedAt
          ? null
          : 'not-allowed',
      feedback: { failure: copy.disconnectFailed },
    },
    retryCleanup: {
      saves: false,
      interaction: { textEditing: 'block' },
      check: (readiness: TestFeedConnectionStatus) =>
        readiness.connectionStatus === 'disabled' &&
        readiness.cleanup?.canRetry === true &&
        readiness.connectionId &&
        readiness.connectionActivatedAt
          ? null
          : 'not-allowed',
      feedback: { failure: copy.cleanupRetryFailed },
    },
  },
})
